/**
 * Turbo Navigation Enhancements — the single owner of navigation STATE.
 *
 * Contract (every other script reads, only this one writes):
 * - html[data-nav-kind]  push | pop | tab | slide-forward | slide-back | none,
 *   stamped on every turbo:visit from the KIND of navigation, never the route.
 *   motion.css keys every View Transition rule on it. Never cleared; the next
 *   visit overwrites it.
 * - html[data-nav-gesture]  set by a gesture controller (swipe-to-view,
 *   edge-swipe-back) right before it triggers a visit; consumed here (the
 *   visit is stamped none) and honoured by skeleton-nav.js (no skeleton).
 * - window.ChippieNav.previousLocation()  where an edge-swipe-back lands.
 * - window.ChippieNav.nativeOwnsBack()  resolves true where the native layer
 *   (shell or browser) animates back itself; restorations are then stamped
 *   none and the web edge-swipe stands down. The compatibility default for a
 *   shell build that predates the ownsBack field lives here and nowhere else.
 * - Not page freshness. static/js/page-check.js is the only owner of "is this
 *   page current", restored pages included. The row refresh after a cached pop
 *   (below) predates it and is a second answer to the same question.
 *
 * Also: navigation loading indicator (dims the target nav link) and the
 * dashboard collapsed-section state applied before render.
 *
 * Scroll restoration is handled natively by Turbo Drive:
 * - Back/forward: restored atomically from snapshot cache
 * - Forward navigation: scrolls to top (standard browser behavior)
 */

(function() {
    'use strict';

    // =========================================================================
    // View Transitions CSS Support
    // =========================================================================
    // View Transition rules and keyframes live in static/css/motion.css
    // (enabled via meta tag in base.html)


    // =========================================================================
    // Navigation Loading Indicator
    // =========================================================================
    // Dims the target nav link on turbo:before-visit for instant visual feedback.
    // Cleared on turbo:render when Turbo swaps in the new page.

    document.addEventListener('turbo:before-visit', function(event) {
        const targetUrl = new URL(event.detail.url);
        const targetPath = targetUrl.pathname;

        // Add loading indicator to the nav link being navigated to
        document.querySelectorAll('.nav-link, .mobile-nav-item').forEach(link => {
            const href = link.getAttribute('href');
            if (!href) return;

            var isTarget = (href === targetPath) ||
                           (targetPath === '/' && href === '/') ||
                           (targetPath.startsWith(href) && href !== '/');

            if (isTarget) {
                link.classList.add('turbo-nav-loading');
            }
        });
    });

    // Remove loading state after render
    document.addEventListener('turbo:render', function(event) {
        document.querySelectorAll('.nav-link.turbo-nav-loading, .mobile-nav-item.turbo-nav-loading').forEach(link => {
            link.classList.remove('turbo-nav-loading');
        });
    });


    // =========================================================================
    // Navigation Kind (single writer of html[data-nav-kind])
    // =========================================================================
    // Every Turbo visit is classified by WHAT KIND of navigation it is, never by
    // which route it targets, and motion.css keys every View Transition rule on
    // that one attribute:
    //   push  advance visits, and forward restorations
    //   pop   back restorations
    //   tab   a lateral switch between bottom-nav sections (no motion): the visit
    //         a nav tap triggers, or any advance visit landing on a nav section
    //   slide-forward / slide-back
    //         a lateral switch between sibling views (the Calendar | Dispatch |
    //         Timesheet tabs) that slides toward the view it goes to. The link
    //         names its side with data-nav-slide="forward|back"; the markup that
    //         owns the ordering decides it, never a list here.
    //   none  replace/refresh visits (SSE catch-up, offline swap, stream
    //         refresh) and visits whose visuals a gesture overlay already owns
    // The stamp is written on turbo:visit, which Turbo dispatches after it has
    // set html[data-turbo-visit-direction] (so back vs forward is known), and it
    // is deliberately NOT cleared on turbo:load: Turbo removes its own direction
    // attribute in visitCompleted, before the transition has finished, and a
    // stamp cleared that early re-selects the default rule mid-animation. The
    // next visit always overwrites it, so no stale kind ever animates.
    //
    // html[data-nav-gesture] is the handshake a gesture controller (swipe-to-view,
    // edge-swipe-back) sets right before it triggers a visit: the overlay owns
    // the visuals, so the visit is stamped none and the flag consumed here.
    //
    // window.ChippieNav.previousLocation() is the URL an edge-swipe-back would
    // land on: a small stack maintained from push/tab (grows) and pop (shrinks);
    // replace visits leave it alone. It mirrors browser history only as far as
    // Turbo visits in this document are concerned, which is all the gesture needs.

    var tabIntentUrl = null;
    var locationStack = [];
    var lastDirection = 'none';   // structural direction of the latest visit (push/pop/none)

    // Who animates a history restoration. Where the native layer already
    // animates back (WKWebView's swipe-back in older iOS shell builds, Safari's
    // swipe-back in the browser), the web must not animate the restoration on
    // top of it: a bitmap of the old page slides in natively, then the real
    // page would slide in again. ChippieBridge.appInfo() owns the fact
    // (ownsBack); the default for a build that predates the field is decided
    // here: iOS => true, everything else => false. Asked a tick after each
    // turbo:load until the bridge answers (its controllers connect after the
    // first turbo:load); the default applies until then.
    var nativeOwnsBack = null;

    function platformIsIos() {
        var bridge = window.ChippieBridge;
        return !!(bridge && bridge._platform && bridge._platform() === 'ios');
    }

    function nativeOwnsBackNow() {
        return nativeOwnsBack === null ? platformIsIos() : nativeOwnsBack;
    }

    var nativeOwnsBackPromise = new Promise(function (resolve) {
        function attempt() {
            setTimeout(function () {
                if (nativeOwnsBack !== null) return;
                var bridge = window.ChippieBridge;
                var query = (bridge && bridge.appInfo) ? bridge.appInfo() : Promise.reject(new Error('app-info-unavailable'));
                query.then(function (info) {
                    if (nativeOwnsBack !== null) return;
                    nativeOwnsBack = (info && typeof info.ownsBack === 'boolean') ? info.ownsBack : platformIsIos();
                    document.removeEventListener('turbo:load', attempt);
                    resolve(nativeOwnsBack);
                }, function () { /* bridge not registered yet: ask again on the next turbo:load */ });
            }, 0);
        }
        document.addEventListener('turbo:load', attempt);
        if (document.readyState !== 'loading') attempt();
    });

    document.addEventListener('turbo:click', function(event) {
        if (event.target.closest && event.target.closest('.mobile-nav-item, .nav-link')) {
            tabIntentUrl = event.detail.url;
        }
    });

    // A click on a link that names its slide side (data-nav-slide). Only the two
    // known sides count; anything else is an ordinary push.
    var slideIntent = null;   // {url, kind}
    document.addEventListener('turbo:click', function(event) {
        var link = event.target.closest && event.target.closest('[data-nav-slide]');
        var side = link && link.getAttribute('data-nav-slide');
        slideIntent = (side === 'forward' || side === 'back')
            ? { url: event.detail.url, kind: 'slide-' + side }
            : null;
    });

    // A section the bottom nav can reach in one tap. The destinations are read
    // from the nav markup itself (templates/components/navbar.html), never from
    // a list repeated here, so adding a tab changes one file. Only the four
    // section anchors match: "More" is a <button> with no href, and the slide-up
    // menu is a sibling of #mobile-navbar, not a child.
    //
    // The native shells hide #mobile-navbar with injected CSS and drive their own
    // tab bar through Turbo.visit(..., {action: 'advance'}) — a programmatic visit
    // dispatches no turbo:click, so nothing sets tabIntentUrl and a tab tap would
    // otherwise be stamped push and animate as a drill-down. The element stays in
    // the DOM (display: none, not removed), which is what makes this lookup work
    // there; a shell that ever stops rendering the web nav gets a push slide on
    // every tab tap.
    function isTopLevelNavDestination(url) {
        var path = new URL(url).pathname;
        return !!document.querySelector('#mobile-navbar a[href="' + path + '"]');
    }

    document.addEventListener('turbo:visit', function(event) {
        var html = document.documentElement;
        var action = event.detail.action;

        // Structural direction first (what history does), then the visual kind
        // (what the screen does). A gesture-owned visit still moves the stack.
        var direction = 'none';
        if (action === 'advance') {
            direction = 'push';
        } else if (action === 'restore') {
            direction = html.getAttribute('data-turbo-visit-direction') === 'back' ? 'pop' : 'push';
        }
        lastDirection = direction;
        if (direction === 'push') {
            locationStack.push(window.location.href);
        } else if (direction === 'pop') {
            locationStack.pop();
        }

        var kind = direction;
        if (html.hasAttribute('data-nav-gesture')) {
            kind = 'none';
            html.removeAttribute('data-nav-gesture');
        } else if (action === 'restore' && nativeOwnsBackNow()) {
            // The native layer already animated this restoration (or nothing
            // did, for an in-page history.back()); never a second motion.
            kind = 'none';
        } else if (action === 'advance' && slideIntent && slideIntent.url === event.detail.url) {
            kind = slideIntent.kind;
        } else if (tabIntentUrl && tabIntentUrl === event.detail.url) {
            kind = 'tab';
        } else if (action === 'advance' && isTopLevelNavDestination(event.detail.url)) {
            // No click intent, but the destination is a bottom-nav section: a
            // lateral switch however it was triggered. Covers the native shells'
            // tab bars, and equally a deep link or notification tap that lands on
            // a section — arriving at a top level is never a drill-down.
            kind = 'tab';
        }
        tabIntentUrl = null;
        slideIntent = null;
        html.setAttribute('data-nav-kind', kind);
    });

    window.ChippieNav = {
        previousLocation: function() {
            return locationStack.length ? locationStack[locationStack.length - 1] : null;
        },
        nativeOwnsBack: function() {
            return nativeOwnsBackPromise;
        }
    };


    // =========================================================================
    // Keeping a restored page fresh
    // =========================================================================
    // A pop is served from Turbo's snapshot cache: instant, but as old as when
    // the user left. Once a cached pop has landed, the row of the entity on the
    // detail page just left is refetched through the row-refresh route and
    // swapped if it differs, so the user's own edit there shows on the list.
    // Skipped when the pop had to fetch anyway (cache miss) or when offline.
    //
    // This is no longer the page's freshness. page-check.js runs on this same
    // turbo:load, asks whether the restored page is current, and swaps the
    // whole body if anything changed, so Back after an edit repaints the row
    // here and then the body there. Do not extend this; a freshness need goes
    // to the page check (docs/technical/features/PWA_CACHE_STRATEGY.md).

    var fetchedDuringVisit = false;
    var loadedPathname = window.location.pathname;   // the page being left on the next visit

    document.addEventListener('turbo:visit', function() {
        fetchedDuringVisit = false;
    });

    document.addEventListener('turbo:before-fetch-request', function(event) {
        if (event.target === document) fetchedDuringVisit = true;
    });

    function isOffline() {
        if (navigator.onLine === false) return true;
        var bar = document.getElementById('offline-indicator');
        // The bar also shows (blue, .is-syncing) while PowerSync syncs online.
        return !!(bar && bar.classList.contains('is-visible') && !bar.classList.contains('is-syncing'));
    }

    document.addEventListener('turbo:load', function() {
        var leftPathname = loadedPathname;
        loadedPathname = window.location.pathname;
        // Structural direction, not the visual kind: a gesture-driven pop is
        // stamped none but still landed on a cached page.
        if (lastDirection !== 'pop' || fetchedDuringVisit || isOffline()) return;
        var entityType = window.entityForDetailPath ? window.entityForDetailPath(leftPathname) : null;
        if (!entityType || !window.SSEEntityUpdates || !window.SSEEntityUpdates.refreshIfChanged) return;
        window.SSEEntityUpdates.refreshIfChanged(entityType, leftPathname.split('/')[2]);
    });


    // =========================================================================
    // Dashboard Collapsed State (Pre-render)
    // =========================================================================
    // Applies collapsed section state BEFORE the new page is rendered
    // This prevents the visual flash of expanded sections on mobile

    const COLLAPSED_STORAGE_KEY = 'dashboard_collapsed_sections';
    const MOBILE_BREAKPOINT = 768;

    function getCollapsedSections() {
        try {
            return JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || '[]');
        } catch (_e) {
            return [];
        }
    }

    // Apply collapsed state to a document (either current or new body)
    function applyCollapsedState(doc) {
        // Only apply on mobile
        if (window.innerWidth >= MOBILE_BREAKPOINT) return;

        const collapsed = getCollapsedSections();
        if (collapsed.length === 0) return;

        collapsed.forEach(function(id) {
            const el = doc.querySelector(id);
            const header = doc.querySelector('[data-bs-target="' + id + '"]');
            if (el) el.classList.remove('show');
            if (header) header.setAttribute('aria-expanded', 'false');
        });
    }

    // Apply collapsed state BEFORE render to prevent flash
    document.addEventListener('turbo:before-render', function(event) {
        // Get the new body that's about to be rendered
        const newBody = event.detail.newBody;
        if (!newBody) return;

        // Check if the new body is a dashboard page (has dashboard section cards)
        const hasDashboardSections = newBody.querySelector('.section-header-collapsible');
        if (!hasDashboardSections) return;

        applyCollapsedState(newBody);
    });

    // Also apply on initial page load (non-Turbo)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            applyCollapsedState(document);
        });
    } else {
        applyCollapsedState(document);
    }

})();
